import { prop } from "ramda";
import React, { useEffect } from "react";

import {
  Box,
  Button,
  Callout,
  Dialog,
  Grouper,
  Intent,
  Label,
  Text,
  TextInput
} from "@blasterjs/core";

import ReasonForChange from "../components/ReasonForChange";

import { DialogBody, DialogFooter, DialogHeader } from "../components/DialogLayout";
import Select, { SelectOfType } from "../components/Select";

import { CaseStatus, formatCaseStatus, Image, ReaderStudyStats, userLabelShort } from "../models";
import { areDifferent } from "../utils";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  addReader,
  assignImage,
  CaseDialogMode,
  CaseDialogState,
  CaseForm,
  caseSearchReadersRequest,
  caseSuggestReadersRequest,
  changeCase,
  closeCaseDialog,
  createCaseRequest,
  dropReader,
  editCaseRequest,
  imagesSearch,
  unassignImage
} from "../slices/caseDialog";
import { RolePermissions } from "../permissions";
import styled from "styled-components";

const RfcBox = styled.div`
  padding-top: 10px;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

interface CaseValidations {
  readonly visitIdLength: string | null;
  readonly visitIdFormat: string | null;
  readonly endoProcIdLength: string | null;
  readonly endoProcIdFormat: string | null;
  readonly histoProcIdLength: string | null;
  readonly histoProcIdFormat: string | null;
  readonly subjectIdLength: string | null;
  readonly subjectIdFormat: string | null;
  readonly siteIdLength: string | null;
  readonly siteIdFormat: string | null;
  readonly errorsExist: boolean;
}

function validateCaseForm(caseDialog: CaseDialogState): CaseValidations {
  function checkTextInputFormat(text: string) {
    if (text.length == 0) {
      return true;
    }

    // Regex pattern that allows:
    // - Alphanumeric characters (a-z, A-Z, 0-9)
    // - Common special characters: space, hyphen, underscore, period, comma, parentheses
    // - Common punctuation: !?@#$%&*
    const pattern = /^[a-zA-Z0-9\s\-_.,'"/()@#%&*]+$/;

    return pattern.test(text);
  }

  const visitIdTooLong = caseDialog.histoCase.data.visitId.value.length > 75;
  const visitIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.visitId.value);
  const endoProcIdTooLong = caseDialog.histoCase.data.procId.value.length > 50;
  const endoProcIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.procId.value);
  const histoProcedureId = caseDialog?.histoCase?.data?.histoProcedureId.value ?? "";
  const histoProcIdTooLong = histoProcedureId.length > 50;
  const histoProcIdFormatError = !checkTextInputFormat(histoProcedureId);
  const subjectIdTooLong = caseDialog.histoCase.data.subjectId.value.length > 30;
  const subjectIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.subjectId.value);
  const siteId = caseDialog?.histoCase?.data?.siteId.value ?? "";
  const siteIdTooLong = siteId.length > 30;
  const siteIdFormatError = !checkTextInputFormat(siteId);

  return {
    visitIdLength: visitIdTooLong ? "Visit ID must be less than 75 characters" : "",
    visitIdFormat: visitIdFormatError ? "Visit ID must be alphanumeric" : "",
    endoProcIdLength: endoProcIdTooLong ? "Endo Procedure ID must be less than 50 characters" : "",
    endoProcIdFormat: endoProcIdFormatError ? "Endo Procedure ID must be alphanumeric" : "",
    histoProcIdLength: histoProcIdTooLong
      ? "Histo Procedure ID must be less than 50 characters"
      : "",
    histoProcIdFormat: histoProcIdFormatError ? "Histo Procedure ID must be alphanumeric" : "",
    subjectIdLength: subjectIdTooLong ? "Subject ID must be less than 30 characters" : "",
    subjectIdFormat: subjectIdFormatError ? "Subject ID must be alphanumeric" : "",
    siteIdLength: siteIdTooLong ? "Site ID must be less than 30 characters" : "",
    siteIdFormat: siteIdFormatError ? "Site ID must be alphanumeric" : "",
    errorsExist:
      visitIdTooLong ||
      visitIdFormatError ||
      endoProcIdTooLong ||
      endoProcIdFormatError ||
      histoProcIdTooLong ||
      histoProcIdFormatError ||
      subjectIdTooLong ||
      subjectIdFormatError ||
      siteIdTooLong ||
      siteIdFormatError
  };
}

function formatReaderStats(stats: ReaderStudyStats): JSX.Element {
  return (
    <>
      <BoldText>{userLabelShort(stats.reader)}</BoldText>&nbsp;({stats.numReadCases}/
      {stats.numTotalCases} cases read)
    </>
  );
}

const SelectReaders = Select as SelectOfType<ReaderStudyStats>;
const SelectImages = Select as SelectOfType<Image>;

const CaseDialog = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const caseDialog = useAppSelector(state => state.caseDialog);
  const caseValidations = useAppSelector(state => validateCaseForm(state.caseDialog));

  useEffect(() => {
    if (caseDialog.histoCase.data.subjectId.value) {
      dispatch(caseSuggestReadersRequest(caseDialog.histoCase.data.subjectId.value));
    }
  }, [caseDialog.savedHistoCase?.caseWithStatus.id]);

  const updateCaseField = <
    Key extends keyof CaseForm<CaseDialogMode>,
    PartialUpdate extends Partial<CaseForm<CaseDialogMode>[Key]>
  >(
    caseKey: Key,
    value: PartialUpdate
  ) => {
    dispatch(
      changeCase({
        ...caseDialog.histoCase.data,
        [caseKey]: {
          ...caseDialog.histoCase.data[caseKey],
          ...value
        }
      })
    );
  };

  const onSubjectIdChange = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const subjectId = e.currentTarget.value.trim();
    dispatch(caseSuggestReadersRequest(subjectId));
    updateCaseField(key, { value: subjectId });
  };

  const updateCaseFieldWithValue = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => updateCaseField(key, { value: e.currentTarget.value });
  const updateCaseFieldWithReasonForChange = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => updateCaseField(key, { reasonForChange: e.currentTarget.value });
  const onReaderNameChange = (value: string) => {
    dispatch(caseSearchReadersRequest({ studyId: caseDialog.studyId, name: value }));
  };
  const onClickSuggestedReader = (reader: ReaderStudyStats) => () => onSelectReader(reader);
  const onSelectReader = (reader: ReaderStudyStats) => {
    dispatch(addReader(reader));
  };
  const onUnassignReader = (reader: ReaderStudyStats) => {
    dispatch(dropReader(reader));
  };
  const onImageNameChange = (value: string) => {
    dispatch(imagesSearch(value));
  };
  const onSelectImage = (image: Image) => {
    dispatch(assignImage(image));
  };
  const onUnassignImage = (image: Image) => {
    dispatch(unassignImage(image));
  };
  const closeDialog = () => {
    dispatch(closeCaseDialog());
  };
  const onSave = () => {
    dispatch(
      caseDialog.mode === CaseDialogMode.OpenForCreate ? createCaseRequest() : editCaseRequest()
    );
  };
  const caseStatus = caseDialog.savedHistoCase && caseDialog.savedHistoCase.caseWithStatus.status;
  const imageAssignmentDisabled =
    caseDialog.mode !== CaseDialogMode.OpenForCreate &&
    !(caseStatus == CaseStatus.PendingLabQC || caseStatus == CaseStatus.PendingQC);
  const disabledText = imageAssignmentDisabled
    ? `Images can only be assigned to cases in ${formatCaseStatus(CaseStatus.PendingQC)}`
    : undefined;
  const errorText =
    "errorMessage" in caseDialog.histoCase ? (
      <Box mb={2}>
        <Callout intent={Intent.DANGER}>
          <Text>{caseDialog.histoCase.errorMessage}</Text>
        </Callout>
      </Box>
    ) : null;
  const readerSuggestionText =
    "resource" in caseDialog.readerSuggestions && caseDialog.readerSuggestions.resource.length ? (
      <Text>
        <Text as="i">
          Suggested reader(s) for subject {caseDialog.histoCase.data.subjectId.value}
        </Text>
        :
        <Grouper>
          {caseDialog.readerSuggestions.resource.map(readerStats => (
            <Button key={readerStats.reader.id} onClick={onClickSuggestedReader(readerStats)}>
              {userLabelShort(readerStats.reader)}
            </Button>
          ))}
        </Grouper>
      </Text>
    ) : null;

  function fieldSaveableCase(
    fieldName: "procId" | "histoProcedureId" | "subjectId" | "siteId" | "visitId"
  ): boolean {
    const isNewCase: boolean = caseDialog.savedHistoCase == null;
    const newValue = caseDialog.histoCase.data[fieldName].value || "";
    const oldValue = caseDialog.savedHistoCase?.caseWithStatus[fieldName] || "";
    const rfc =
      ("reasonForChange" in caseDialog.histoCase.data[fieldName] &&
        (caseDialog.histoCase.data[fieldName] as any).reasonForChange) ||
      "";
    const updateSavable = oldValue == newValue || (oldValue != newValue && rfc.length > 0);
    return isNewCase || updateSavable;
  }

  function fieldSaveableCaseMany(fieldName: "images" | "readers"): boolean {
    const isNewCase: boolean = caseDialog.savedHistoCase == null;
    const newValue = caseDialog.histoCase.data[fieldName].value || [];
    const oldValue =
      (caseDialog &&
        caseDialog.savedHistoCase &&
        fieldName in caseDialog.savedHistoCase &&
        caseDialog.savedHistoCase[fieldName]) ||
      [];
    const rfc =
      ("reasonForChange" in caseDialog.histoCase.data[fieldName] &&
        (caseDialog.histoCase.data[fieldName] as any).reasonForChange) ||
      "";
    const updateSavable = oldValue == newValue || (oldValue != newValue && rfc.length > 0);
    return isNewCase || updateSavable;
  }

  const disableSave: boolean =
    caseValidations.errorsExist ||
    !fieldSaveableCase("procId") ||
    !fieldSaveableCase("histoProcedureId") ||
    !fieldSaveableCase("subjectId") ||
    !fieldSaveableCase("siteId") ||
    !fieldSaveableCase("visitId") ||
    !fieldSaveableCaseMany("images") ||
    !fieldSaveableCaseMany("readers");

  return (
    <Dialog
      isOpen={
        caseDialog.mode !== CaseDialogMode.Closed &&
        // Avoid flicker by waiting to open dialog until case has loaded
        !("isPending" in caseDialog.histoCase)
      }
      onRequestClose={closeDialog}
      appElementId="root"
      width="500px"
    >
      <DialogHeader
        title={(caseDialog.mode === CaseDialogMode.OpenForCreate ? "Create" : "Edit") + " Case"}
        closeDialog={closeDialog}
      />
      <DialogBody>
        {errorText}
        <Box>
          <Label>
            Endo Procedure ID
            <TextInput
              placeholder="Endo Procedure ID"
              value={caseDialog.histoCase.data.procId.value}
              onChange={updateCaseFieldWithValue("procId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.endoProcIdLength}
              {caseValidations.endoProcIdLength ? <br /> : <></>}
              {caseValidations.endoProcIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.procId !==
          caseDialog.histoCase.data.procId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="procId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.procId
                  ? caseDialog.histoCase.data.procId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("procId")}
            />
          </RfcBox>
        ) : null}
        {caseDialog.mode === CaseDialogMode.OpenForCreate ? (
          <></>
        ) : (
          <>
            <Box mt={2}>
              <Label>
                Histo Procedure ID
                <TextInput
                  placeholder="Histo Procedure ID"
                  value={caseDialog.histoCase.data.histoProcedureId.value || ""}
                  onChange={updateCaseFieldWithValue("histoProcedureId")}
                />
                <Box
                  className="validationMessage"
                  style={{
                    display: caseValidations.errorsExist ? "block" : "none"
                  }}
                >
                  {caseValidations.histoProcIdLength}
                  {caseValidations.histoProcIdLength ? <br /> : <></>}
                  {caseValidations.histoProcIdFormat}
                </Box>
              </Label>
            </Box>
            {caseDialog.mode === CaseDialogMode.OpenForEdit &&
            caseDialog.savedHistoCase?.caseWithStatus.histoProcedureId !==
              caseDialog.histoCase.data.histoProcedureId.value ? (
              <RfcBox>
                <ReasonForChange
                  rkey="histoProcedureId_reason"
                  mb={2}
                  value={
                    "reasonForChange" in caseDialog.histoCase.data.histoProcedureId
                      ? caseDialog.histoCase.data.histoProcedureId.reasonForChange
                      : ""
                  }
                  onChange={updateCaseFieldWithReasonForChange("histoProcedureId")}
                />
              </RfcBox>
            ) : null}
          </>
        )}
        <Box mt={2}>
          <Label>
            Subject ID
            <br />
            <TextInput
              placeholder="Subject ID"
              value={caseDialog.histoCase.data.subjectId.value}
              onChange={onSubjectIdChange("subjectId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.subjectIdLength}
              {caseValidations.subjectIdLength ? <br /> : <></>}
              {caseValidations.subjectIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.subjectId !==
          caseDialog.histoCase.data.subjectId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="subjectId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.subjectId
                  ? caseDialog.histoCase.data.subjectId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("subjectId")}
            />
          </RfcBox>
        ) : null}
        <Box mt={2}>
          <Label>
            Site ID
            <br />
            <TextInput
              placeholder="Site ID"
              value={caseDialog.histoCase.data.siteId.value || ""}
              onChange={updateCaseFieldWithValue("siteId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.siteIdLength}
              {caseValidations.siteIdLength ? <br /> : <></>}
              {caseValidations.siteIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.siteId !==
          caseDialog.histoCase.data.siteId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="siteId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.siteId
                  ? caseDialog.histoCase.data.siteId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("siteId")}
            />
          </RfcBox>
        ) : null}
        <Box mt={2}>
          <Label>
            Visit ID
            <br />
            <TextInput
              placeholder="Visit ID"
              value={caseDialog.histoCase.data.visitId.value}
              onChange={updateCaseFieldWithValue("visitId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.visitIdLength}
              {caseValidations.visitIdLength ? <br /> : <></>}
              {caseValidations.visitIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.visitId !==
          caseDialog.histoCase.data.visitId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="visitId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.visitId
                  ? caseDialog.histoCase.data.visitId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("visitId")}
            />
          </RfcBox>
        ) : null}
        <hr style={{ marginTop: "1.6rem" }} />
        <Box mb={2}>{readerSuggestionText}</Box>
        <Box display="block" justifyContent="space-between" mt={2}>
          <Box style={{ width: "100%" }}>
            <Label>
              Assign Readers
              <SelectReaders
                disabled={
                  !(
                    "resource" in loggedInUser &&
                    loggedInUser.resource.can([RolePermissions.AP_CaseViewer_ManualCRassignment])
                  )
                }
                placeholder={"Enter user’s name"}
                searchText={caseDialog.readersSearchText}
                searchResults={
                  "resource" in caseDialog.readersSearchResults
                    ? caseDialog.readersSearchResults.resource
                    : []
                }
                onSelect={onSelectReader}
                onChangeSearchText={onReaderNameChange}
                onDeselect={onUnassignReader}
                selectedItems={
                  caseDialog.histoCase.data ? caseDialog.histoCase.data.readers.value : []
                }
                isLoading={
                  "isPending" in caseDialog.readersSearchResults
                    ? caseDialog.readersSearchResults.isPending
                    : false
                }
                format={formatReaderStats}
              />
            </Label>
          </Box>
          {caseDialog.mode === CaseDialogMode.OpenForEdit &&
          caseDialog.savedHistoCase &&
          areDifferent(
            caseDialog.savedHistoCase.readers.map(readerStats => readerStats.reader.id),
            caseDialog.histoCase.data.readers.value.map(readerStats => readerStats.reader.id)
          ) ? (
            <RfcBox>
              <ReasonForChange
                rkey="readers_reason"
                mb={2}
                value={
                  "reasonForChange" in caseDialog.histoCase.data.readers
                    ? caseDialog.histoCase.data.readers.reasonForChange
                    : ""
                }
                onChange={updateCaseFieldWithReasonForChange("readers")}
              />
            </RfcBox>
          ) : null}
        </Box>
        <hr style={{ marginTop: "1.6rem" }} />
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box width="100%">
            <Label>
              Assign Images
              <SelectImages
                placeholder={"Enter image file name"}
                searchText={caseDialog.imagesSearchText}
                searchResults={
                  "resource" in caseDialog.imagesSearchResults
                    ? caseDialog.imagesSearchResults.resource
                    : []
                }
                onSelect={onSelectImage}
                onChangeSearchText={onImageNameChange}
                onDeselect={onUnassignImage}
                selectedItems={
                  caseDialog.histoCase.data ? caseDialog.histoCase.data.images.value : []
                }
                isLoading={
                  "isPending" in caseDialog.imagesSearchResults &&
                  caseDialog.imagesSearchResults.isPending
                }
                format={prop("name")}
                disabled={imageAssignmentDisabled}
                disabledText={disabledText}
              />
            </Label>
          </Box>
          {caseDialog.mode === CaseDialogMode.OpenForEdit &&
          caseDialog.savedHistoCase &&
          areDifferent(
            caseDialog.savedHistoCase.images.map(image => image.id),
            caseDialog.histoCase.data.images.value.map(image => image.id)
          ) ? (
            <RfcBox>
              <ReasonForChange
                rkey="images_reason"
                mb={2}
                value={
                  "reasonForChange" in caseDialog.histoCase.data.images
                    ? caseDialog.histoCase.data.images.reasonForChange
                    : ""
                }
                onChange={updateCaseFieldWithReasonForChange("images")}
              />
            </RfcBox>
          ) : null}
        </Box>
      </DialogBody>
      <DialogFooter>
        <Button
          intent="primary"
          appearance="prominent"
          onClick={onSave}
          isLoading={"isSaving" in caseDialog.histoCase}
          disabled={disableSave}
        >
          Save
        </Button>
        <Button onClick={closeDialog}>Cancel</Button>
      </DialogFooter>
    </Dialog>
  );
};

export default CaseDialog;
